import React from 'react'

import { Status } from '@/components/status'
import { DATE_FORMAT_WITH_TIME, formatDate } from '@/helpers/date'
import { isEmpty, isNil } from '@/helpers/type-guards'
import { YesNoNaChoice } from '@/models/back-office-pages/annual-review'
import { ApproverDTO } from '@/models/back-office-pages/prospects'
import { CustomCountries } from '@/models/countries'
import { TFunc } from '@/translations/types'

// Scrolls to object by provided id
export const scrollTo = (id: string): void => {
  const target = document.getElementById(id)

  return target ? target.scrollIntoView({ behavior: 'smooth', block: 'start' }) : null
}

// Copy text to Clipboard
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // Fallback for older browsers or non-HTTPS contexts
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.cssText = 'position:fixed;opacity:0;pointer-events:none'
    document.body.appendChild(textarea)
    textarea.select()
    const success = document.execCommand('copy')
    document.body.removeChild(textarea)
    return success
  }
}

export const mergeArrays = <T extends object>(
  array1: T[],
  array2: T[],
  ids: Record<string, boolean>,
  mergeByName: keyof T & string = 'id' as keyof T & string,
): T[] => {
  // Combine both arrays into a single array
  const combined = [...array1, ...array2]
  // Filter the combined array to keep only objects with ids in the ids object
  const filtered = combined.filter(obj => {
    const keyVal = (obj as any)[mergeByName] as string | undefined
    return !!keyVal && Object.prototype.hasOwnProperty.call(ids, keyVal)
  })

  const seen = new Set<string>()
  const result: T[] = []

  for (const obj of filtered) {
    const keyVal = (obj as any)[mergeByName] as string
    if (!seen.has(keyVal)) {
      seen.add(keyVal)
      result.push(obj)
    }
  }

  return result
}

interface Address {
  fullAddress?: string
  street1?: string
  street2?: string
  city?: string
  postalCode?: string
  countryCode: string
  t: TFunc
}

export const getFullAddress = ({ fullAddress, street1, street2, city, countryCode, postalCode, t }: Address): string => {
  const hasCustomCountry = !!countryCode && Object.prototype.hasOwnProperty.call(CustomCountries, countryCode)

  const country = !countryCode ? '' : hasCustomCountry ? t(`country.${countryCode}_name`) : String(countryCode)

  const baseParts = fullAddress?.trim() ? [fullAddress.trim()] : [street1, street2, city, postalCode].map(v => v?.trim()).filter(Boolean)

  const parts = [...baseParts, country.trim()].filter(Boolean)

  return parts.join(', ')
}

export const getFullName = (name: string | null, surname: string | null): string => {
  const firstName = name && name.trim() ? `${name.trim()} ` : ''
  const lastName = surname && surname.trim() ? surname.trim() : ''

  return `${firstName}${lastName}`
}

export const getApproversList = (approvers: Array<ApproverDTO>, t: TFunc): string =>
  approvers
    ?.map(({ approverEmail, approved }) =>
      t('approval.APPROVED_status_description', {
        user: approverEmail,
        date: formatDate(approved, { dateFormat: DATE_FORMAT_WITH_TIME }),
      }),
    )
    .join(', ')

export const getYesNoOptions = (t: TFunc): Array<{ label: string; value: boolean }> => [
  { label: t('global.yes_option'), value: true },
  { label: t('global.no_option'), value: false },
]

export const getYesNoValue = (value: any, t: TFunc): string =>
  !isNil(value) && !isEmpty(value) ? t(value ? 'global.yes_option' : 'global.no_option') : undefined

export const getYesNoNAOptions = (t: TFunc): Array<{ label: string; value: YesNoNaChoice }> => [
  { label: t('global.yes_option'), value: YesNoNaChoice.YES },
  { label: t('global.no_option'), value: YesNoNaChoice.NO },
  { label: t('global.na_option'), value: YesNoNaChoice.NA },
]

export const getEnabledDisabledValue = (value: boolean | null | undefined, t: TFunc) =>
  !isNil(value) && !isEmpty(value) && t(value ? 'global.enabled_option' : 'global.disabled_option')

export const getEnabledDisabledOptions = (t: TFunc): Array<{ label: string; value: boolean }> => [
  { label: t('global.enabled_option'), value: true },
  { label: t('global.disabled_option'), value: false },
]

export const getYesNoNAValue = (value: YesNoNaChoice, t: TFunc): string =>
  !isNil(value) && !isEmpty(value) ? t(`global.${value.toLocaleLowerCase()}_option`) : undefined

export const getFieldCount = (index: number, length: number) => (length > 1 ? index + 1 : '')

export const getStatusFilterOptions = (obj: { [key: string]: string }) =>
  Object.values(obj).map(value => ({
    value: value,
    label: <Status status={value} width="fit-content" />,
  }))

type Procedure<T extends unknown[]> = (...args: T) => void

export const debounce = <T extends unknown[]>(func: Procedure<T>, wait: number): Procedure<T> => {
  let timeout: ReturnType<typeof setTimeout> | undefined

  return function executedFunction(...args: T) {
    const later = () => {
      if (timeout !== undefined) {
        clearTimeout(timeout)
      }
      func(...args)
    }

    if (timeout !== undefined) {
      clearTimeout(timeout)
    }

    timeout = setTimeout(later, wait)
  }
}

type StripTypename<T> = T extends (infer U)[]
  ? StripTypename<U>[]
  : T extends object
    ? { [K in keyof T as K extends '__typename' ? never : K]: StripTypename<T[K]> }
    : T

type StripTypenameFn = <T>(obj: T) => StripTypename<T>
export type WithTypename<T> = T & { __typename?: string }

export const stripTypename: StripTypenameFn = obj => {
  if (obj === null || typeof obj !== 'object') {
    return obj as StripTypename<typeof obj>
  }
  if (Array.isArray(obj)) {
    return obj.map(item => stripTypename(item)) as StripTypename<typeof obj>
  }

  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (k === '__typename') continue
    out[k] = stripTypename(v)
  }
  return out as StripTypename<typeof obj>
}

export const safeJoin = (parts: Array<string | null | undefined>, sep = ', ') =>
  parts
    .map(p => (typeof p === 'string' ? p.trim() : ''))
    .filter(Boolean)
    .join(sep)
